import { useEffect, useState } from 'react'
import { Button, Sheet } from '@/design-system'
import { hasSeenWhatsNew } from '@/features/whatsnew/whatsnew'
import { enablePush, pushSupport } from './push'
import './push-invite.css'

const SEEN_KEY = 'catgo.push-invite-seen'

/**
 * La invitación a los avisos.
 *
 * El interruptor de notificaciones vivía enterrado en Ajustes y nadie lo
 * encontraba: de siete personas registradas, solo dos los tenían activados, y a
 * las otras cinco nunca se les había ofrecido. Sin avisos no hay forma de
 * decirle a nadie que alguien quiere seguirle.
 *
 * Se enseña UNA vez, y solo a quien puede decir que sí:
 *   · pushSupport() === 'default' descarta a quien ya los tiene (Álvaro y
 *     Brenda), a quien ya dijo que no en el navegador — volver a preguntar no
 *     abre ningún diálogo, solo molesta — y al iPhone que aún no ha instalado
 *     la app, que no puede ni pedirlo.
 *   · si la hoja de novedades tiene algo que contar, hoy le toca a ella: dos
 *     hojas seguidas al abrir es una emboscada.
 *
 * El permiso de verdad lo pide el navegador, y solo lo hace si sale de un toque
 * de la persona: por eso hay un botón y no una petición automática al arrancar.
 */
export function PushInviteGate() {
  const [open, setOpen] = useState(false)
  const [pidiendo, setPidiendo] = useState(false)

  useEffect(() => {
    if (!hasSeenWhatsNew()) return
    if (pushSupport() !== 'default') return
    try {
      if (localStorage.getItem(SEEN_KEY)) return
    } catch {
      return // sin memoria no se puede prometer «una sola vez»
    }
    const timer = setTimeout(() => setOpen(true), 900)
    return () => clearTimeout(timer)
  }, [])

  const cerrar = () => {
    try {
      localStorage.setItem(SEEN_KEY, new Date().toISOString())
    } catch {
      /* se habrá preguntado una vez de más, no es grave */
    }
    setOpen(false)
  }

  const aceptar = async () => {
    setPidiendo(true)
    try {
      await enablePush()
    } catch {
      /* si el navegador se niega, el interruptor sigue en Ajustes */
    }
    setPidiendo(false)
    cerrar()
  }

  return (
    <Sheet open={open} onClose={cerrar}>
      <div className="pinv">
        <span className="pinv__disc" aria-hidden="true">
          🔔
        </span>
        <h2 className="pinv__title">¿Te avisamos?</h2>
        <p className="pinv__body">
          Solo de lo que pasa en tu colección: cuando alguien quiera seguirte, cuando acepten tu
          solicitud y cuando reconozcamos a uno de tus gatos en la foto de otra persona.
        </p>
        <p className="pinv__nota">
          Nada de novedades ni recordatorios. Se apaga en Ajustes cuando quieras.
        </p>
        <div className="pinv__acts">
          <Button block size="lg" loading={pidiendo} onClick={() => void aceptar()}>
            Avísame
          </Button>
          <Button block variant="ghost" onClick={cerrar}>
            Ahora no
          </Button>
        </div>
      </div>
    </Sheet>
  )
}
